import React from "react";
import { AlertTriangle, CheckCircle2, Shield } from "lucide-react";

import { resolveMediaUrl } from "../lib/fileUrl";
import { displayOdometerKm } from "../lib/kmUnits";
import { inspectionAiStatusBanner, isAiManualReviewStatus } from "../lib/inspectionAiStatus";

function severityColor(sev) {
  if (sev === "major") return "var(--zc-danger, #dc2626)";
  if (sev === "moderate") return "var(--zc-warn, #f59e0b)";
  return "var(--zc-text-3)";
}

export default function InspectionReport({
  inspection,
  compact = false,
  listedExteriorColor,
  listedInteriorColor,
  showPlateDetails = true,
}) {
  if (!inspection) return null;
  const ai = inspection.ai_assessment || {};
  const damages = ai.damages || [];
  const newDamage = ai.new_damage_vs_baseline || [];
  const score = ai.condition_score;
  const pendingBanner = inspectionAiStatusBanner(ai);
  const awaitingManualReview = isAiManualReviewStatus(ai);

  return (
    <div className={compact ? "" : "zc-card p-5"} data-testid={`inspection-report-${inspection.inspection_id}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="eyebrow capitalize">{inspection.phase?.replace("_", " ")}</div>
          <div className="text-sm font-semibold mt-1 flex items-center gap-2">
            <Shield size={14} className="text-[var(--zc-accent)]" />
            Smart condition assessment
          </div>
        </div>
        {score != null && (
          <div className="text-right">
            <div className="zc-stat text-2xl" style={{ color: score >= 80 ? "var(--zc-success)" : score >= 60 ? "var(--zc-warn)" : "var(--zc-danger)" }}>
              {score}
            </div>
            <div className="eyebrow">Score</div>
          </div>
        )}
      </div>

      {pendingBanner && (
        <div
          className="mt-3 p-3 rounded-lg border border-[var(--zc-warn)]/50 bg-[var(--zc-warn)]/10"
          data-testid="inspection-ai-pending-banner"
          role="status"
        >
          <div className="flex items-start gap-2 text-sm font-semibold text-[var(--zc-text)]">
            <AlertTriangle size={14} className="text-[var(--zc-warn)] shrink-0 mt-0.5" />
            {pendingBanner.title}
          </div>
          <p className="text-xs text-[var(--zc-text-2)] mt-1.5 leading-relaxed">{pendingBanner.body}</p>
        </div>
      )}

      {ai.summary && !awaitingManualReview && (
        <p className="text-sm text-[var(--zc-text-2)] mt-3 leading-relaxed">{ai.summary}</p>
      )}

      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-[var(--zc-text-3)]">
        {displayOdometerKm(inspection) != null && (
          <span>Odometer: {displayOdometerKm(inspection).toLocaleString()} km</span>
        )}
        {inspection.fuel_pct != null && (
          <span>{inspection.fuel_pct}% fuel/charge</span>
        )}
        {ai.detected_exterior_color && (
          <span>Exterior detected: {ai.detected_exterior_color}</span>
        )}
        {listedExteriorColor && (
          <span>Exterior listed: {listedExteriorColor}</span>
        )}
        {ai.detected_interior_color && (
          <span>Interior detected: {ai.detected_interior_color}</span>
        )}
        {listedInteriorColor && (
          <span>Interior listed: {listedInteriorColor}</span>
        )}
        {ai.exterior_matches_listing === false && (
          <span className="text-[var(--zc-danger,#dc2626)] font-semibold">Exterior color mismatch</span>
        )}
        {ai.interior_matches_listing === false && (
          <span className="text-[var(--zc-danger,#dc2626)] font-semibold">Interior color mismatch</span>
        )}
        {ai.identity_matches_listing === false && (
          <span className="text-[var(--zc-danger,#dc2626)] font-semibold">Vehicle identity mismatch</span>
        )}
        {ai.identity_matches_listing === true && ai.identity_confidence != null && (
          <span className="text-[var(--zc-success)]">Identity verified ({ai.identity_confidence}%)</span>
        )}
        {showPlateDetails && ai.detected_plate && (
          <span>
            Plate read: <span className="font-mono">{ai.detected_plate}</span>
            {ai.plate_confidence != null && <> ({ai.plate_confidence}% confidence)</>}
          </span>
        )}
        {showPlateDetails && ai.plate_matches_listing === false && (
          <span className="text-[var(--zc-danger,#dc2626)] font-semibold">Plate does not match listing</span>
        )}
        {showPlateDetails && ai.plate_matches_pickup === false && (
          <span className="text-[var(--zc-danger,#dc2626)] font-semibold">Plate does not match pick-up</span>
        )}
        {showPlateDetails && (ai.plate_matches_listing === true || ai.plate_matches_pickup === true) && (
          <span className="text-[var(--zc-success)]">Plate verified</span>
        )}
      </div>

      {awaitingManualReview ? null : (damages.length > 0 || newDamage.length > 0) ? (
        <div className="mt-4 space-y-2">
          <div className="text-xs font-semibold uppercase tracking-widest text-[var(--zc-text-3)] flex items-center gap-1.5">
            <AlertTriangle size={12} /> Issues detected
          </div>
          {[...damages, ...newDamage].map((d, i) => (
            <div key={i} className="text-xs p-2.5 rounded-lg bg-[var(--zc-bg-2)] border border-[var(--zc-border)]">
              <span className="font-semibold capitalize" style={{ color: severityColor(d.severity) }}>
                {d.severity || "note"}
              </span>
              {d.area && <span className="text-[var(--zc-text)]"> · {d.area}</span>}
              {d.description && <div className="text-[var(--zc-text-2)] mt-1">{d.description}</div>}
            </div>
          ))}
        </div>
      ) : (
        <div className="mt-3 flex items-center gap-2 text-xs text-[var(--zc-success)]">
          <CheckCircle2 size={14} /> No significant damage detected in photos
        </div>
      )}

      {!compact && inspection.photos?.length > 0 && (
        <div className="grid grid-cols-3 gap-2 mt-4">
          {inspection.photos.map(p => (
            <div key={p.file_id} className="relative">
              <img
                src={resolveMediaUrl(p.url || `/api/files/${p.file_id}`)}
                alt={p.angle}
                className="w-full aspect-[4/3] object-contain bg-[var(--zc-bg-2)] rounded-md border border-[var(--zc-border)]"
              />
              <span className="absolute bottom-1 left-1 px-1.5 py-0.5 text-2xs font-bold uppercase bg-black/60 text-white rounded">
                {p.angle?.replace("_", " ")}
              </span>
            </div>
          ))}
        </div>
      )}

      {displayOdometerKm(inspection) != null && compact && (
        <div className="text-xs text-[var(--zc-text-3)] mt-3">Odometer: {displayOdometerKm(inspection).toLocaleString()} km</div>
      )}
    </div>
  );
}
